import React, { FC, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { getDealerInformation } from '@utils/getStockDealerInformations';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import Maps from '@components/CheckoutDelivery/components/CheckoutMap';
import DealerDuck from 'src/redux/dealer/dealer.duck';
import { Link } from '@components/v2/link';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { CONTACT_OPTIONS } from 'src/constants/enums';
import InfoButton from '@components/SummaryDealerDetails/InfoButton';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { Pages } from 'src/types/pages';
import { isBrandAC, isBrandAP, isBrandDS, isBrandOV, isMarketGB, LEAD_FORM_ID, ModalVersion } from 'src/constants/main';
import { useRouter } from 'next/router';
import { SummaryPriceDetailsProps } from '.';
import IconButton from '@components/IconButton';
import Tooltip, { Effect } from '@components/GlobalTooltip';
import { Divider } from 'src/pages/summary';
import { usePlausible, UserEvent } from '@hooks/usePlausible';

const SummaryDealerDetailsTemplate: FC<SummaryPriceDetailsProps> = ({ className, generatedIndex, page }) => {
    const { t } = useTranslations();
    const { carDetails } = useCarDetailsDuck();
    const dispatch = useDispatch();
    const router = useRouter();
    const { plausibleUserEvent } = usePlausible();

    const makeARequestBlackList: string[] = ['/configurable', '/stock', '/trim/stock/[...model]'];

    const isLeadFormContactEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_CONTACT_OPTIONS,
        CONTACT_OPTIONS.LEAD_FORM
    );

    const dealer = useSelector((state) => DealerDuck.getOwnState(state).dealer);

    const [isOpen, setIsOpen] = useState(true);

    const { street, siretNumber, dealerName, phoneNumber, coordinates, openingHours } = getDealerInformation(
        carDetails,
        dealer
    );

    const isOpeningHoursAvailable = openingHours?.split('<br />')?.length;

    const bookATestDrive = () => {
        window.location.href = t('modal.needAHand.testDrive.url');
    };

    const loadLeadForm = () => {
        dispatch(
            UIDuck.openModal({
                modalType: UIDuck.MODAL_TYPES.LEAD_FORM,
                data: {
                    page: Pages.basketV2,
                    leadFormId: LEAD_FORM_ID,
                    type: 'LEAD_FORM',
                },
                modalVersion: ModalVersion.v2,
            })
        );
    };

    const handleFindDealerModal = () =>
        dispatch(
            UIDuck.openModal({
                data: { data: {} },
                callbacks: { onClick: () => console.log('') },
                modalType: UIDuck.MODAL_TYPES.FIND_DEALER,
                modalVersion: ModalVersion.v2,
            })
        );

    const isTestDriveContactEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_CONTACT_OPTIONS,
        CONTACT_OPTIONS.TEST_DRIVE
    );

    return (
        <div
            data-testid="dealer"
            className={`dealerInformation ${isOpen ? `${className} isOpen` : className}`}
            id="dealer"
        >
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton className="titleWrapperButton" onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            {generatedIndex ? (
                                <div className="titleContainer">
                                    <span className="sectionNumber">{generatedIndex}.</span>
                                    <span className="title">{`${t('basket.dealer.title').toLowerCase()}`}</span>
                                </div>
                            ) : (
                                <span className="title">{`${t('basket.dealer.title')}`.toLowerCase()}</span>
                            )}
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                <div className="container">
                    <div className="info">
                        <div className="addr">
                            {dealerName && <span className="name">{dealerName.toLowerCase()}</span>}
                            {!!street && <span>{street.toLowerCase()}</span>}
                            <span>{phoneNumber}</span>
                        </div>
                        {!!siretNumber && (
                            <span className="item">{`${t('checkout.user.details.businessID')}: ${siretNumber}`}</span>
                        )}
                        {isOpeningHoursAvailable && (
                            <span className="item">
                                {t('hoursOfOperation')}
                                <InfoButton
                                    data-for="dealerToolTip"
                                    data-html="true"
                                    data-tip={`<div style="text-align: left">${openingHours}</div>`}
                                    onClick={Pages.basketV2 !== page && handleFindDealerModal}
                                />
                                {Pages.basketV2 === page && (
                                    <Tooltip
                                        id="dealerToolTip"
                                        effect={Effect.solid}
                                        scrollHide={false}
                                        event="click"
                                        eventOff="mouseout blur"
                                        delayHide={300}
                                        afterShow={() => plausibleUserEvent(UserEvent.ShowOpeningHoursOnDealerPage)}
                                    />
                                )}
                            </span>
                        )}
                        {isTestDriveContactEnabled && (
                            <Link
                                iconName={Icons.ChevronRight}
                                data-testid="notReadyToBuyOnlineCallUsAt"
                                className="linkButton"
                                txt={t('notReadyToBuyOnlineBookATestDrive')}
                                onClick={bookATestDrive}
                            >
                                <button onClick={bookATestDrive} className="linkButton">
                                    <span className="txt">{t('notReadyToBuyOnlineBookATestDrive')}</span>
                                    <Icon
                                        name={Icons.ChevronRight}
                                        className="chevron-right-thin"
                                        width={12}
                                        height={10}
                                    />
                                </button>
                            </Link>
                        )}
                        {!makeARequestBlackList.includes(router.pathname) && isLeadFormContactEnabled && (
                            <Link
                                iconName={Icons.ChevronRight}
                                data-testid="notReadyToBuyOnlineCallUsAt"
                                className="linkButton"
                                txt={t('notReadyToBuyOnlineWriteUs')}
                                onClick={loadLeadForm}
                            >
                                <button onClick={loadLeadForm} className="linkButton">
                                    <span className="txt">{t('notReadyToBuyOnlineWriteUs')}</span>
                                    <Icon
                                        name={Icons.ChevronRight}
                                        className="chevron-right-thin"
                                        width={12}
                                        height={10}
                                    />
                                </button>
                            </Link>
                        )}
                        <Link
                            iconName={Icons.ChevronRight}
                            data-testid="notReadyToBuyOnlineCallUsAt"
                            className="linkButton viewOnMap"
                            txt={t('viewOnMap')}
                            onClick={handleFindDealerModal}
                        >
                            <button onClick={handleFindDealerModal} className="linkButton viewOnMap">
                                <span className="txt">{t('viewOnMap')}</span>
                                <Icon name={Icons.ChevronRight} className="chevron-right-thin" width={12} height={10} />
                            </button>
                        </Link>
                    </div>
                    {coordinates?.lat && coordinates?.lon && (
                        <Maps latitude={coordinates?.lat} longitude={coordinates?.lon} zoom={15} />
                    )}
                </div>
            </Collapsible>
            {Pages.summary === page && <Divider isBottom isOpen={isOpen} />}
        </div>
    );
};

export default SummaryDealerDetailsTemplate;
